import { Modal } from '@arco-design/web-react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Area } from 'react-easy-crop/types';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { base64ToFile, fileToBase64, getCroppedImg } from '../AvatarCropDialog/helpers';
import { ImageCropperPane } from '../ImageCrop/ImageCropperPane';
import './EventImageCropDialog.css';

const DEFAULT_EVENT_IMAGE_ASPECT = 16 / 9;

type Props = {
  visible: boolean;
  file: File;
  onCancel: () => void;
  onConfirm: (file: File) => void;
  aspect?: number;
  titleKey?: string;
  helpKey?: string;
  confirmKey?: string;
};

export function EventImageCropDialog({
  visible,
  file,
  onConfirm,
  onCancel,
  aspect = DEFAULT_EVENT_IMAGE_ASPECT,
  titleKey = 'events.event_image_crop_title',
  helpKey = 'events.event_image_crop_help',
  confirmKey = 'events.event_image_crop_confirm',
}: Props) {
  const { t } = useTranslation();
  const isMobileCrop = useMediaQuery({ maxWidth: 767.99 });
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [rotation, setRotation] = useState(0);

  const onCropSubmit = async () => {
    if (!croppedAreaPixels || !imageSrc) {
      return;
    }

    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      if (!croppedImage) {
        return;
      }
      const croppedFile = base64ToFile(croppedImage, file.name, file.type);
      onConfirm(croppedFile);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    const timer = setTimeout(() => {
      fileToBase64(file)
        .then((imageDataUrl) => {
          if (typeof imageDataUrl === 'string') {
            setImageSrc(imageDataUrl);
          }
        })
        .catch(() => {
          console.warn('failed to load image for crop');
        });
    }, 200);

    return () => clearTimeout(timer);
  }, [file, visible]);

  useEffect(() => {
    if (!visible) {
      setImageSrc(null);
      setCroppedAreaPixels(null);
      setRotation(0);
    }
  }, [visible]);

  const isWide = aspect >= 2;

  return (
    <Modal
      className={classNames('event-image-crop-modal', {
        'event-image-crop-modal--mobile': isMobileCrop,
        'event-image-crop-modal--wide': isWide,
      })}
      wrapClassName={classNames('event-image-crop-modal-wrapper', {
        'event-image-crop-modal-wrapper--mobile': isMobileCrop,
        'event-image-crop-modal-wrapper--wide': isWide,
      })}
      title={t(titleKey)}
      visible={visible}
      okText={t(confirmKey)}
      onCancel={onCancel}
      onConfirm={onCropSubmit}
      alignCenter
      getPopupContainer={() => document.body}
      unmountOnExit
    >
      <p className="event-image-crop-modal__help">{t(helpKey)}</p>
      {visible && imageSrc && (
        <ImageCropperPane
          imageSrc={imageSrc}
          aspect={aspect}
          variant={isWide ? 'wide' : 'default'}
          onCropComplete={() => undefined}
          onCropAreaChange={(pixels, nextRotation) => {
            setCroppedAreaPixels(pixels);
            setRotation(nextRotation);
          }}
        />
      )}
    </Modal>
  );
}
